/**
 * Client-side teardown and reload used by the deployment refresh framework.
 *
 * Ported from wlth-growth-hub (`apps/web/src/lib/appUpdate.ts`) and trimmed: this app
 * no longer ships a caching service worker, so there is no waiting-worker handshake
 * to perform. What remains is the safety-critical part — clear anything that could
 * serve an old bundle, then reload, with a watchdog so a hung teardown can never
 * leave the user sitting on a stale client.
 */

/** Hard cap on teardown before we reload regardless. */
const TEARDOWN_TIMEOUT_MS = 1200
/** Watchdog: reload even if the teardown promise never settles. */
const WATCHDOG_MS = 1500

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      window.setTimeout(() => resolve(undefined), timeoutMs)
    }),
  ])
}

/**
 * Drop every service worker registration and Cache Storage bucket.
 *
 * Normally a no-op: the app registers no service worker. It still runs because a
 * device may carry a registration from the retired precaching worker, and because
 * a third party (or a previous deploy) can leave a bucket behind.
 */
export async function clearClientCaches(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    } catch (error) {
      console.error('[app-update] service worker unregister failed', error)
    }
  }

  if ('caches' in window) {
    try {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    } catch (error) {
      console.error('[app-update] cache clear failed', error)
    }
  }
}

/**
 * Reload, revalidating the document.
 *
 * A plain reload is enough here: with no service worker intercepting, the only
 * remaining layer is the HTTP cache, and the deploy pipeline already purges
 * Cloudflare for the zone on every release (see the `clear-cache` job).
 */
export function hardReloadApplication(): void {
  window.location.reload()
}

/** Clear what can serve a stale bundle, then reload. Always reloads. */
export async function refreshApplication(): Promise<void> {
  const watchdog = window.setTimeout(hardReloadApplication, WATCHDOG_MS)

  try {
    await withTimeout(clearClientCaches(), TEARDOWN_TIMEOUT_MS)
  } catch (error) {
    console.error('[app-update] teardown failed, reloading anyway', error)
  }

  window.clearTimeout(watchdog)
  hardReloadApplication()
}
